
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { ContactData, FAQ, SocialMedia, TeamMember } from '@/types/contact';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MapPin, Mail, Phone, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import LogoScroller from '@/components/LogoScroller';

const convertContactData = (data: any): ContactData => ({
  ...data,
  faq: data.faq ? JSON.parse(data.faq) : [],
  social_media: data.social_media ? JSON.parse(data.social_media) : {},
  team_members: data.team_members ? JSON.parse(data.team_members) : []
});

const defaultContactData: ContactData = {
  conference_id: null,
  email: "info@aiconference.london",
  phone: "+442037735519",
  address: "20-22 Wenlock Road, London N1 7GU, United Kingdom",
  social_media: {
    twitter: "https://twitter.com/aiconference",
    facebook: "https://facebook.com/aiconference",
    linkedin: "https://linkedin.com/company/aiconference",
    instagram: "https://instagram.com/aiconference"
  },
  contact_form_enabled: true,
  faq: [
    {
      question: 'When does the conference start?',
      answer: 'The AI Conference London - AI World Congress 2026 starts on November 25th, 2026 at 9:00 AM.'
    },
    {
      question: 'Are meals included with tickets?',
      answer: 'Yes, all ticket types include lunch and refreshments throughout the day.'
    },
    {
      question: 'Can I get a refund if I can\'t attend?',
      answer: 'Full 100% refund within 14 days of booking. After that our terms apply. You can also transfer your ticket to someone else.'
    }
  ],
  team_members: [
    {
      name: 'Alex Johnson',
      role: 'Conference Director',
      email: 'alex@aiconference.london',
      image_url: 'https://i.pravatar.cc/300?img=12'
    },
    {
      name: 'Sarah Smith',
      role: 'Speaker Relations',
      email: 'sarah@aiconference.london',
      image_url: 'https://i.pravatar.cc/300?img=32'
    },
    {
      name: 'Michael Chen',
      role: 'Sponsorship Manager',
      email: 'michael@aiconference.london',
      image_url: 'https://i.pravatar.cc/300?img=59'
    }
  ]
};

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactData, setContactData] = useState<ContactData>(defaultContactData);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        const { data, error } = await supabase
          .from('contact_details')
          .select('*')
          .is('conference_id', null)
          .maybeSingle();

        if (error) throw error;
        
        if (data) {
          setContactData(convertContactData(data));
        }
      } catch (error) {
        console.error('Error fetching contact data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContactData();

    if (location.hash === '#faq') {
      const faqSection = document.getElementById('faq-section');
      if (faqSection) {
        faqSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert([formData]);

      if (dbError) throw dbError;

      const response = await supabase.functions.invoke('contact-form', {
        body: formData
      });

      if (response.error) throw response.error;

      toast({
        title: "Message Sent Successfully!",
        description: "We've received your message and will get back to you soon.",
      });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error Sending Message",
        description: "There was a problem sending your message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ai-dark">
        <Header />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ai-dark">
      <Helmet>
        <title>Contact Us - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content="Contact AI World Congress 2026. Get in touch for speaker inquiries, sponsorship opportunities, or general questions about the London AI conference." />
        <link rel="canonical" href="https://aiconference.london/contact" />
        <meta property="og:title" content="Contact Us - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta property="og:description" content="Get in touch with AI World Congress 2026 for speaker inquiries, sponsorship, or questions." />
        <meta property="og:url" content="https://aiconference.london/contact" />
        <meta property="og:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta name="twitter:description" content="Get in touch with AI World Congress 2026." />
        <meta name="twitter:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
      </Helmet>
      <Header />
      <PageHeader
        title=""
        subtitle=""
        backgroundImage="/lovable-uploads/530fddbf-4fea-4198-860f-8165d2f8b445.png"
        className="bg-gradient-to-b from-purple-900 to-black"
      />
      <main className="pt-8">
        <section className="py-16 bg-gradient-to-b from-black to-gray-900">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {contactData.contact_form_enabled && (
                <div>
                  <h2 className="text-3xl font-bold text-white mb-8">Get in Touch</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                        Your Name
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                        Email Address
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">
                        Subject
                      </label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        required
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Your message here..."
                        required
                        className="bg-gray-800 border-gray-700 text-white min-h-[150px]"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </div>
              )}
              
              <div className="space-y-8">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-8">Contact Information</h2>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <MapPin className="text-purple-500 h-6 w-6 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white">Address</h3>
                        <p className="text-gray-300">{contactData.address}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <Mail className="text-purple-500 h-6 w-6 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white">Email</h3>
                        <p className="text-gray-300">{contactData.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <Phone className="text-purple-500 h-6 w-6 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white">Phone</h3>
                        <p className="text-gray-300">{contactData.phone}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <Clock className="text-purple-500 h-6 w-6 mt-1" />
                      <div>
                        <h3 className="text-xl font-semibold text-white">Office Hours</h3>
                        <p className="text-gray-300">Monday - Friday: 9:00 AM - 6:00 PM</p>
                        <p className="text-gray-300">Saturday - Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="rounded-lg overflow-hidden h-64 mt-8">
                  <iframe 
                    title="Office Location" 
                    className="w-full h-full border-0"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2058.682375144876!2d-0.09368370000000001!3d51.530578999999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761b50f85f9245%3A0x709184b054d23e98!2sCompanies%20MadeSimple!5e1!3m2!1sen!2suk!4v1745555811217!5m2!1sen!2suk"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                
                {contactData.faq.length > 0 && (
                  <section id="faq-section" className="bg-gray-900 rounded-lg p-6 mt-8">
                    <h3 className="text-xl font-semibold text-white mb-4">FAQs</h3>
                    <div className="space-y-4">
                      {contactData.faq.slice(0, 3).map((item, index) => (
                        <div key={index} className="border-b border-gray-800 pb-4 last:border-0 last:pb-0">
                          <h4 className="font-medium text-white mb-2">{item.question}</h4>
                          <p className="text-gray-400">{item.answer}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <LogoScroller />
      <Footer />
    </div>
  );
};

export default Contact;
